import type { OgsmBoard, OgsmBoardNode } from '@/types/api'

export interface OgsmSearch {
  session?: string
  objective?: string
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// These ids go straight into API paths, so anything but a UUID is dropped.
function uuidOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && UUID.test(value) ? value : undefined
}

export function parseOgsmSearch(search: Record<string, unknown>): OgsmSearch {
  return {
    objective: uuidOrUndefined(search.objective),
    session: uuidOrUndefined(search.session),
  }
}

/** Root-first chain from the top objective down to the node, the node included; [] when not found. */
export function drilledPath(
  board: OgsmBoard,
  nodeId: string | undefined
): OgsmBoardNode[] {
  if (!nodeId) return []
  const walk = (nodes: OgsmBoardNode[]): OgsmBoardNode[] => {
    for (const node of nodes) {
      if (node.id === nodeId) return [node]
      const below = walk(node.strategies)
      if (below.length > 0) return [node, ...below]
    }
    return []
  }
  return walk(board.objectives)
}

/** Any node in the tree; a stale link to one that no longer exists falls back to the root. */
export function findDrilledObjective(
  board: OgsmBoard,
  objectiveId: string | undefined
): OgsmBoardNode | undefined {
  return drilledPath(board, objectiveId).at(-1)
}
